import { css } from "@emotion/css";
import { space } from "@octopusdeploy/design-system-tokens";
import { type PropertyValueResource } from "@octopusdeploy/octopus-server-client";
import React from "react";
import ChangedValueIndicator from "~/areas/projects/components/Variables/TenantVariables/ChangedValueIndicator";
import DefaultValueIndicator from "~/areas/projects/components/Variables/TenantVariables/DefaultValueIndicator";
import ReadonlyInlineVariableValue from "~/areas/projects/components/Variables/TenantVariables/ReadonlyInlineVariableValue";
import { getVariableTypeFromDisplaySettings } from "~/areas/projects/components/Variables/TenantVariables/getVariableTypeFromDisplaySettings";
import type { LoadedSourceItems } from "~/areas/projects/components/Variables/TenantVariables/useTenantVariableResourceOptions";
import type { DoBusyTask } from "~/components/DataBaseComponent";
import PopoverWhenFocused from "~/components/PopoverWhenFocused";
import type { PopoverPosition } from "~/components/PopoverWhenFocused/PopoverWhenFocused";
import TenantVariableInput from "~/components/TenantVariableInput/TenantVariableInput";
import type { RowData } from "./ScopedTenantTemplateDataGrid";
export interface TenantTemplateValueCellProps {
    item: RowData;
    enabled: boolean;
    isFocused: boolean;
    onFocus: () => void;
    onBlur: () => void;
    onChange: (value: PropertyValueResource | undefined) => void;
    // Input props
    sourceItems: LoadedSourceItems;
    localNames: string[];
    doBusyTask: DoBusyTask;
}
export function TenantTemplateValueCell(props: TenantTemplateValueCellProps) {
    const { item, enabled, isFocused, onFocus, onBlur, onChange, ...inputProps } = props;
    const valueOrDefault = item.Value ?? item.Template.DefaultValue ?? "";
    const missingVariable = valueOrDefault === "";
    const isInheritingDefault = !item.Value && !!item.Template.DefaultValue;
    const accountIndices = inputProps.sourceItems.accounts.items.reduce((acc, account) => ({ ...acc, [account.Id]: account.Name }), {});
    const certificateIndices = inputProps.sourceItems.certificates.loadedItems.reduce((acc, certificate) => ({ ...acc, [certificate.Id]: certificate.Name }), {});
    const workerPoolIndices = inputProps.sourceItems.workerPools.loadedItems.reduce((acc, workerPool) => ({ ...acc, [workerPool.Id]: workerPool }), {});
    const variableType = getVariableTypeFromDisplaySettings(item.Template.DisplaySettings["Octopus.ControlType"]);
    const calculateFocusedPopoverPosition = (): PopoverPosition => {
        return {
            top: -12,
            left: 0,
            right: 0,
        };
    };
    return enabled ? (<div onFocus={onFocus} onClick={onFocus} className={cellStyles.editableContainer}>
            <PopoverWhenFocused onClickOutside={onBlur} isFocused={isFocused} dynamicPosition={calculateFocusedPopoverPosition}>
                {isFocused ? (<div className={cellStyles.focusedContainer}>
                        <TenantVariableInput {...inputProps} variableTemplate={item.Template} value={item.Value} warning={missingVariable ? "Value required for deployment" : undefined} onChange={onChange} hideSearch={true} hideLabel={true} autoFocus={true}/>
                    </div>) : (<div className={cellStyles.readonlyContainer}>
                        <ReadonlyInlineVariableValue accounts={accountIndices} certificates={certificateIndices} workerPools={workerPoolIndices} value={item.Value} defaultValue={item.Template.DefaultValue} variableType={variableType} hasEditPermissions={true} sourceItems={inputProps.sourceItems}/>
                        <ChangedValueIndicator show={item.HasValueChanged}/>
                        <DefaultValueIndicator show={isInheritingDefault}/>
                    </div>)}
            </PopoverWhenFocused>
        </div>) : (<div className={cellStyles.readonlyContainer}>
            <ReadonlyInlineVariableValue accounts={accountIndices} certificates={certificateIndices} workerPools={workerPoolIndices} value={item.Value} defaultValue={item.Template.DefaultValue} variableType={variableType} hasEditPermissions={false} sourceItems={inputProps.sourceItems}/>
        </div>);
}
const cellStyles = {
    editableContainer: css({
        display: "flex",
        alignItems: "center",
        width: "100%",
        height: "4rem",
    }),
    focusedContainer: css({
        padding: space[8],
    }),
    readonlyContainer: css({
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: space[8],
        width: "100%",
        height: "100%",
        padding: `${space[8]} ${space[12]}`,
    }),
};
